import { useMutation, useQueryClient } from '@tanstack/react-query';
import { chestService } from '@services';
import { USER_PROFILE_QUERY_KEY } from './useUserProfile';
import { UNOPENED_CHESTS_QUERY_KEY } from './useUnopenedChests';

interface VerifyChestPurchaseOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  maxAttempts?: number;
  pollingInterval?: number;
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useVerifyChestPurchase = (
  options: VerifyChestPurchaseOptions = {}
) => {
  const {
    onSuccess,
    onError,
    maxAttempts = 5,
    pollingInterval = 3000,
  } = options;

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (txHash: string) => {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const response = await chestService.verifyTransaction(txHash);

          // Validate response structure - must have key fields from backend
          const isValidResponse =
            response &&
            typeof response === 'object' &&
            response.id &&
            response.buyer &&
            response.txHash &&
            response.chestId !== undefined;

          if (isValidResponse) {
            return response;
          } else {
            throw new Error('Backend has not synced transaction yet');
          }
        } catch (error: any) {
          const isLastAttempt = attempt === maxAttempts;

          if (isLastAttempt) {
            throw new Error(
              'Unable to verify purchase. The blockchain network may be experiencing issues. Please check your inventory later.'
            );
          } else {
            await delay(pollingInterval);
          }
        }
      }

      throw new Error('Polling completed unexpectedly');
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEY });
      await queryClient.invalidateQueries({
        queryKey: UNOPENED_CHESTS_QUERY_KEY,
      });

      onSuccess?.();
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
};
